<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="breck-school-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <p>
              Last Month, 75 Breck School students gathered in the auditorium
              for a 30-minute live drone flight over Hawaii's Koko Crater
              region. Instead of a canned video, they heard a real-time feed
              while their teacher moved around the room with a wireless mic,
              relaying each question to our pilot. That simple setup—just a
              laptop, a stable connection, and a microphone—turned an ordinary
              lesson into a dynamic exploration.
            </p>
          </section>

          <section class="blog-section">
            <h2>Hanauma Bay: Marine Life in Focus</h2>
            <p>
              We began over Hanauma Bay's clear turquoise waters. As soon as
              fish darted into view, the first question came through: "What is
              Hawaii's state fish?" Within seconds our pilot answered
              "humuhumunukunukuāpuaʻa," and you could feel the room brighten.
              Seeing those fish in their natural habitat sparked more questions
              about reef health and conservation—questions that led to a lively
              lunchtime debate on protecting marine life back home. That level
              of sustained attention and real-world context is hard to achieve
              with prerecorded clips.
            </p>
          </section>

          <section class="blog-section">
            <h2>Koko Crater: Geology and Local History</h2>
            <p>
              Next, we climbed the slopes of Koko Crater. A student asked, "When
              did Koko Crater last erupt?" Moments later, our pilot explained
              its Holocene origins. That naturally led to "Why do volcanoes
              erupt?" and a straightforward overview of magma and tectonic
              plates. Then came, "Can you talk more about the
              erosion?"—referring to the water erosion still visible on the
              crater's flank. Teachers paused briefly to tie these answers back
              to curriculum standards in earth science and local history, then
              opened the floor for student reflections on how geography shapes
              the history of the islands.
            </p>
          </section>

          <section class="blog-section">
            <h2>The Blow Hole: A Shared Moment of Wonder</h2>
            <p>
              Our final stop was the coastal blow hole. When seawater burst
              sky-high, students gasped and applauded in unison. That
              spontaneous reaction—unfiltered by editing—highlighted the power
              of live learning. Teachers later remarked that few outdoor lessons
              had ever held attention so completely, and that the blow hole's
              unpredictable beauty created a teachable moment about wave
              pressure and rock erosion they'll revisit all year.
            </p>
          </section>

          <section class="blog-section">
            <h2>Why This Works—and Why Schools Should Adopt It</h2>
            <ol class="benefits-list">
              <li>
                <strong>Authentic Engagement</strong>
                <p>
                  Real-time Q&A keeps students invested. When they know their
                  questions shape the tour, they stay focused—and curious—much
                  longer than in a typical video or field trip.
                </p>
              </li>
              <li>
                <strong>Curriculum Alignment</strong>
                <p>
                  Each live segment can be mapped to your standards—volcanic
                  processes, marine ecology, local history—making lesson
                  planning seamless and assessments clear.
                </p>
              </li>
              <li>
                <strong>Scalable & Low-Logistics</strong>
                <p>
                  No buses, permission slips, or weather worries. All you need
                  is a screen, a mic, and an internet connection. That
                  simplicity lets you schedule more frequent "field trips"
                  without draining budgets or staff time.
                </p>
              </li>
              <li>
                <strong>Memorable Experiences</strong>
                <p>
                  Shared live moments—like naming the state fish or witnessing
                  the blow hole—spark conversations that continue well after
                  class ends. Students were still talking about the tour during
                  lunchtime, sketching maps and planning imaginary trips to
                  Hawaii.
                </p>
              </li>
              <li>
                <strong>Marketing & Enrollment</strong>
                <p>
                  Offering live drone tours signals innovation, attracts
                  families looking for experiential learning, and provides
                  unique content for school newsletters, social media, and
                  open-house events.
                </p>
              </li>
            </ol>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Conclusion</h2>
            <p>
              Breck School's live drone adventure wasn't just a one-off event;
              it demonstrated how real-time exploration ignites curiosity,
              reinforces learning, and delivers measurable engagement. If you're
              ready to bring your curriculum to life—and give your students an
              experience they'll talk about for weeks—explore Skyhost's live
              drone tours at
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >.
            </p>
            <p>
              Give your school the edge of true exploration—no passports
              required.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "BreckSchoolBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "breck-school-tour";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .filter((blog) => blog.tags.some((tag) => this.tags.includes(tag)))
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.breck-school-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

.benefits-list {
  padding-left: 1.5rem;
}

.benefits-list li {
  margin-bottom: 1.5rem;
}

.benefits-list strong {
  font-size: 1.1rem;
  color: #000;
}

.benefits-list p {
  margin-top: 0.5rem;
  margin-bottom: 0;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
